import { z } from 'zod';
import { parseSchema } from '@kirin/api-client/errors';

/**
 * `/help/manifest.json`, written by the docs site's manifest plugin
 * (`apps/docs/plugins/help-manifest/manifest.ts`); keep the two in step. The
 * docs are built into the app's `dist/help/`, so this is a same-origin static
 * file, not an API call.
 */
export const HELP_BASE_PATH = '/help/';
export const DOCS_MANIFEST_URL = `${HELP_BASE_PATH}manifest.json`;

const DocSectionSchema = z.object({
  /** Heading id in the page: `${path}#${id}`. */
  id: z.string(),
  title: z.string(),
});

const ManifestDocSchema = z.object({
  key: z.string(),
  path: z.string().startsWith(HELP_BASE_PATH),
  title: z.string(),
  helpSummary: z.string().nullable(),
  /** The per-screen help's "On this screen" list. */
  helpItems: z.array(z.string()),
  /** The page's `##` sections, for the full-docs navigation. */
  sections: z.array(DocSectionSchema),
  /** The docs section (sidebar category) the doc sits in, e.g. "Integrations". */
  category: z.string().nullable(),
  featureFlag: z.string().nullable(),
  screenKeys: z.array(z.string()),
  lastUpdated: z.string().nullable(),
});

export const DocsManifestSchema = z.object({
  version: z.literal(1),
  /** In the docs' own navigation order. */
  docs: z.array(ManifestDocSchema),
});

export type DocsManifest = z.infer<typeof DocsManifestSchema>;
export type ManifestDoc = z.infer<typeof ManifestDocSchema>;
export type DocSection = z.infer<typeof DocSectionSchema>;

export async function fetchDocsManifest(
  signal?: AbortSignal
): Promise<DocsManifest> {
  const response = await fetch(DOCS_MANIFEST_URL, {
    signal,
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`Docs manifest request failed (${response.status})`);
  }
  return parseSchema(DocsManifestSchema, await response.json());
}
